/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { App, reactive, Ref } from "vue";
import {
  EventMessage,
  EventMessageUtils,
  EventType,
  InteractionStatus,
  PublicClientApplication,
  AuthenticationResult,
  AccountInfo,
  PopupRequest,
  RedirectRequest,
  AuthError,
  SilentRequest,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { config, loginRequest } from "./config";

export type MsalContext = {
  authenticated: Ref<boolean>;
  instance: PublicClientApplication;
  user: Ref<Record<string, string>>;
};

export interface IdToken {
  given_name: string;
  family_name: string;
  country: string;
  extension_Birthdate: string;
  extension_Nationality: string;
  extension_PhoneNumber: string;
  emails: string[];
}

export type MsalAuthenticationResult = {
  acquireToken(
    requestOverride?: PopupRequest | RedirectRequest | SilentRequest
  ): void;
  result: Ref<AuthenticationResult | null>;
  error: Ref<AuthError | null>;
  inProgress: Ref<boolean>;
};

export async function acquireToken() {
  try {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest
    });
    return response.idToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect(loginRequest);
    }
    throw error;
  }
}


type AccountIdentifiers = Partial<Pick<AccountInfo, "homeAccountId" | "localAccountId" | "username">>;

/**
 * Helper function to determine whether 2 arrays are equal
 * Used to avoid unnecessary state updates
 * @param arrayA
 * @param arrayB
 */
function accountArraysAreEqual(arrayA: Array<AccountIdentifiers>, arrayB: Array<AccountIdentifiers>): boolean {
  if (arrayA.length !== arrayB.length) {
    return false;
  }

  const comparisonArray = [...arrayB];

  return arrayA.every((elementA) => {
    const elementB = comparisonArray.shift();
    if (!elementA || !elementB) {
      return false;
    }

    return (
      elementA.homeAccountId === elementB.homeAccountId &&
      elementA.localAccountId === elementB.localAccountId &&
      elementA.username === elementB.username
    );
  });
}



export const msalInstance = new PublicClientApplication(config);

export const msalPlugin = {
  install: (app: App, msalInstance: PublicClientApplication) => {
    const inProgress = InteractionStatus.Startup;
    const accounts = msalInstance.getAllAccounts();

    const state = reactive({
      instance: msalInstance,
      inProgress: inProgress,
      accounts: accounts
    });

    app.config.globalProperties.$msal = state;

    msalInstance.addEventCallback((message: EventMessage) => {
      switch (message.eventType) {
        case EventType.ACCOUNT_ADDED:
        case EventType.ACCOUNT_REMOVED:
        case EventType.LOGIN_SUCCESS:
        case EventType.SSO_SILENT_SUCCESS:
        case EventType.HANDLE_REDIRECT_END:
        case EventType.LOGIN_FAILURE:
        case EventType.SSO_SILENT_FAILURE:
        case EventType.LOGOUT_END:
        case EventType.ACQUIRE_TOKEN_SUCCESS:
        case EventType.ACQUIRE_TOKEN_FAILURE:
          {
            const currentAccounts = msalInstance.getAllAccounts();
            if (!accountArraysAreEqual(currentAccounts, state.accounts)) {
              state.accounts = currentAccounts;
            }
            break;
          }
      }

      const status = EventMessageUtils.getInteractionStatusFromEvent(message, state.inProgress);
      if (status !== null) {
        state.inProgress = status;
      }
    });
  }
}
